import type { AxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { uploadVideoInputImage } from '../api/generateVideo.api';
import type { GenerateVideoApiError, UploadedImage, VideoOperation } from '../types';

export interface PendingImage {
  localId: string;
  fileName: string;
  previewUrl: string;
  status: 'uploading' | 'uploaded' | 'failed';
  uploaded?: UploadedImage;
  error?: string;
}

/** Images picked for image_to_video: previewed locally, uploaded to R2 right away. */
export function useImageUploads(operation: VideoOperation) {
  const [images, setImages] = useState<PendingImage[]>([]);
  const previews = useRef(new Set<string>());

  useEffect(() => {
    const urls = previews.current;
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const update = (localId: string, patch: Partial<PendingImage>) =>
    setImages((current) =>
      current.map((image) => (image.localId === localId ? { ...image, ...patch } : image))
    );

  const add = useCallback(
    (files: File[]) => {
      for (const file of files) {
        const localId = crypto.randomUUID();
        const previewUrl = URL.createObjectURL(file);
        previews.current.add(previewUrl);
        setImages((current) => [
          ...current,
          { localId, fileName: file.name, previewUrl, status: 'uploading' },
        ]);
        uploadVideoInputImage(file, operation)
          .then((uploaded) => update(localId, { status: 'uploaded', uploaded }))
          .catch((error: AxiosError<GenerateVideoApiError>) =>
            update(localId, { status: 'failed', error: error.response?.data?.message })
          );
      }
    },
    [operation]
  );

  const remove = useCallback((localId: string) => {
    setImages((current) => {
      const removed = current.find((image) => image.localId === localId);
      if (removed) {
        URL.revokeObjectURL(removed.previewUrl);
        previews.current.delete(removed.previewUrl);
      }
      return current.filter((image) => image.localId !== localId);
    });
  }, []);

  const clear = useCallback(() => {
    setImages((current) => {
      current.forEach((image) => {
        URL.revokeObjectURL(image.previewUrl);
        previews.current.delete(image.previewUrl);
      });
      return [];
    });
  }, []);

  return { images, add, remove, clear };
}
